import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  PaymentMethod,
  PaymentPlanDetailsResponse,
  PaymentPlanFrequency,
  updatePaymentPlan,
  PaymentPlanUpdateReviewResponse,
  reviewUpdatePaymentPlan,
  PaymentPlanStatus,
  PaymentPlanConfirmationStatus,
} from '@frontend/api-payment-plans';
import { getUser } from '@frontend/auth-helpers';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import {
  PaymentMethodName,
  getPaymentMethodName,
  useGetPatientPaymentMethods,
  usePaymentPlanCardOnFile,
} from '@frontend/payments-card-on-file';
import { theme } from '@frontend/theme';
import {
  DatePickerField,
  DropdownField,
  EmailField,
  Modal,
  MoneyField,
  NumberField,
  Text,
  TextField,
  TextareaField,
  useForm,
  useModalControl,
  useAlert,
  ModalControlModalProps,
  Button,
} from '@frontend/design-system';
import { toCurrency, convertToCents } from '../../utils/currency';
import { formatCentsToCurrency } from '../../utils/currency-helpers';
import {
  createFutureISODateTimeInUTC,
  isValidUnixDate,
  isoDateToDisplayInUTC,
  isoDateToDisplayLocalString,
  planFrequencyLabel,
  termOptions,
} from '../../utils/date-helpers';
import { PaymentPlanPaymentSummary } from '../PaymentPlanPaymentSummary';
import { EditBillingMethodModal } from './EditBillingMethodModal';
import { PlanChangesModal } from './PlanChangesModal';

export const EditPaymentPlanModal = ({
  planDetails,
  type = 'edit',
  modalProps,
  onPlanUpdated,
}: {
  onPlanUpdated: (workflowId: string) => void;
  type?: 'edit' | 'resume';
  planDetails: PaymentPlanDetailsResponse;
  modalProps: ModalControlModalProps & { maxWidth?: number; minWidth?: number };
}) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const { paymentMethods } = useGetPatientPaymentMethods(planDetails.person.id, planDetails.locationId);
  const { modalProps: addCardModalProps, triggerProps: addCardTriggerProps } = useModalControl();
  const { modalProps: confirmChangesModalProps, triggerProps: confirmChangesTriggerProps } = useModalControl();

  const initalPaymentMethodId = planDetails?.paymentMethod?.paymentMethodId;
  const [patientPaymentMethodId, setPatientPaymentMethodId] = useState(initalPaymentMethodId);
  const [reviewUpdates, setReviewUpdates] = useState<PaymentPlanDetailsResponse>();

  const isEditOperation = type === 'edit';
  const nextPaymentAt = planDetails?.schedule?.nextPaymentAt;
  dayjs.extend(isSameOrAfter);
  const today = dayjs().utc(true).toString();
  const nextPaymentAtDate =
    nextPaymentAt && isValidUnixDate(nextPaymentAt) && dayjs(nextPaymentAt).isSameOrAfter(today)
      ? isoDateToDisplayInUTC(nextPaymentAt)
      : isoDateToDisplayInUTC(today);

  const { getFieldProps, reset, values, changedValues, isComplete } = useForm({
    fields: {
      planName: {
        type: 'text',
        value: planDetails.name,
      },
      customerEmailId: {
        type: 'email',
        value: '',
      },
      planAmount: {
        type: 'money',
        min: 0.5,
        value: toCurrency(planDetails.schedule.amount.toString()),
      },
      frequency: {
        type: 'dropdown',
        value: planDetails.schedule.frequency,
        required: true,
      },
      paymentTerm: {
        type: 'number',
        value: (planDetails?.schedule.paymentTerm ?? 0).toString(),
        required: true,
        min: 1,
        max: 60,
      },
      paymentDate: {
        type: 'datePicker',
        value: nextPaymentAtDate,
      },
      paymentMethod: {
        type: 'dropdown',
        value: initalPaymentMethodId,
      },
      memo: {
        type: 'text',
      },
    },
    computeChangedValues: true,
  });

  const selectedPaymentMethod = paymentMethods?.find((pm) => pm.id === patientPaymentMethodId);

  useEffect(() => {
    if (values.paymentMethod) setPatientPaymentMethodId(values.paymentMethod);
  }, [values?.paymentMethod]);

  const changedFields = useMemo(
    () =>
      changedValues
        ? Object.keys(changedValues).filter((key) => !['customerEmailId', 'planName', 'memo'].includes(key))
        : [],
    [changedValues]
  );
  const noFieldsModified =
    changedFields.length === 0 &&
    getFieldProps('paymentDate').value !== isoDateToDisplayInUTC(today) &&
    !changedFields.includes('paymentDate') &&
    type !== 'resume';

  const onlyBillingMethodChanged = useMemo(
    () => changedFields.includes('paymentMethod') && changedFields.length === 1,
    [changedFields]
  );
  const confirmationRequiringFieldsModified = !noFieldsModified && !onlyBillingMethodChanged;
  const requireEmail =
    confirmationRequiringFieldsModified ||
    !isEditOperation ||
    planDetails?.confirmationDetails.confirmationStatus !== PaymentPlanConfirmationStatus.Accept;

  const getTermPayment = () => Number(values?.planAmount) / Number(values?.paymentTerm) || 0;

  const authedUser = getUser();

  const { editBillingMethodProps } = usePaymentPlanCardOnFile({
    locationId: planDetails.locationId,
    paymentAmount: convertToCents(getTermPayment()),
    patientId: planDetails.person.id,
    patientEmail: planDetails.person.email || '',
    paymentFrequency: values.frequency as PaymentPlanFrequency,
  });

  const disableSelectedACHPaymentMethod = selectedPaymentMethod?.type === 'us_bank_account';

  const disableSaveBtn =
    !isComplete ||
    noFieldsModified ||
    (requireEmail && !values.customerEmailId) ||
    disableSelectedACHPaymentMethod ||
    !selectedPaymentMethod;

  const handleSetBillingMethod = async (selectedPaymentMethod: PaymentMethod) => {
    setPatientPaymentMethodId(selectedPaymentMethod.id);
    addCardModalProps.onClose();
  };

  const handleUpdatePaymentPlan = async () => {
    try {
      const {
        data: { workflowId },
      } = await updatePaymentPlan(planDetails.id, {
        amount: convertToCents(Number(values.planAmount)),
        frequency: values.frequency as PaymentPlanFrequency,
        paymentMethodId: patientPaymentMethodId,
        ...(requireEmail
          ? {
              customerEmailId: values.customerEmailId,
              status: PaymentPlanStatus.Paused,
              confirmationStatus: PaymentPlanConfirmationStatus.Pending,
              expireLink: false,
            }
          : {}),
        paymentTerm: Number(values.paymentTerm),
        ...(values.paymentDate ? { startAt: createFutureISODateTimeInUTC(values.paymentDate || '') } : {}),
        notes: values.memo,
      });
      onPlanUpdated(workflowId);
      modalProps.onClose();
      confirmChangesModalProps.onClose();
      alert.success(t('Plan updated successfully!'));
    } catch (err) {
      console.log('update error: ', err);
      alert.error(t('Error updating payment plan. Please try again.'));
    }
  };

  const getUpdateReview = async () => {
    try {
      const updates: PaymentPlanUpdateReviewResponse = await reviewUpdatePaymentPlan(planDetails.id, {
        amount: convertToCents(Number(values.planAmount)),
        frequency: values.frequency as PaymentPlanFrequency,
        paymentMethodId: patientPaymentMethodId,
        paymentTerm: Number(values.paymentTerm),
        ...(values.paymentDate ? { startAt: createFutureISODateTimeInUTC(values.paymentDate || '') } : {}),
        notes: values.memo,
      });

      setReviewUpdates(updates.newPlan);
      confirmChangesTriggerProps.onClick();
    } catch (err) {
      if (err instanceof HttpError) alert.error(err.message);
      console.log('update review error: ', err);
    }
  };

  return (
    <>
      <Modal {...modalProps}>
        <Modal.Header textAlign='left'>
          {isEditOperation ? t('Edit Payment Plan') : t('Resume Payment Plan')}
        </Modal.Header>
        <Modal.Body>
          <>
            <div
              css={css`
                display: grid;
                grid-template-columns: 250px 200px;
                gap: ${theme.spacing(2)};
              `}
            >
              <div
                css={css`
                  display: grid;
                  grid-template-rows: repeat(auto-fill, 40px);
                  gap: ${theme.spacing(2)};
                  align-items: center;

                  & > div {
                    max-width: 250px;
                  }
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-flow: row;
                    justify-content: space-between;
                    align-items: center;
                  `}
                >
                  <Text
                    weight='bold'
                    css={css`
                      font-size: ${theme.fontSize(18)};
                    `}
                  >
                    {t('New')}
                  </Text>
                  <Button
                    variant='tertiary'
                    onClick={() => reset()}
                    iconName='update'
                    color={theme.colors.primary50}
                    css={css`
                      color: ${theme.colors.primary50};
                      svg {
                        height: ${theme.fontSize(16)};
                        width: ${theme.fontSize(16)};
                      }
                    `}
                  >
                    {t('Reset')}
                  </Button>
                </div>
                <TextField {...getFieldProps('planName')} label={t('Payment Plan Name')} />
                <MoneyField {...getFieldProps('planAmount')} label={t('Original Balance')} />

                <DropdownField label={t('Payment Cadence *')} {...getFieldProps('frequency')}>
                  {termOptions.map((term) => (
                    <DropdownField.Option key={term.value} value={term.value}>
                      {term.displayValue}
                    </DropdownField.Option>
                  ))}
                </DropdownField>
                <NumberField label={t('Number of Payments *')} {...getFieldProps('paymentTerm')} />
                <DatePickerField label={t('Next Payment Due')} {...getFieldProps('paymentDate')} />
                <DropdownField label={t('Billing Method')} {...getFieldProps('paymentMethod')}>
                  {paymentMethods?.map((pm, index) => {
                    const optionDisabled = pm.type === 'us_bank_account';
                    return (
                      <DropdownField.Option key={`${pm.id}-${index}`} value={pm.id} disabled={optionDisabled}>
                        {getPaymentMethodName(pm)}
                      </DropdownField.Option>
                    );
                  })}
                  <DropdownField.Option value='' {...addCardTriggerProps}>
                    {t('Add new card')}
                  </DropdownField.Option>
                </DropdownField>
              </div>
              <div
                css={css`
                  display: grid;
                  grid-template-rows: repeat(auto-fill, 40px);
                  gap: ${theme.spacing(2)};
                  align-items: center;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: start;
                    height: ${theme.spacing(3.75)};
                  `}
                >
                  <Text
                    weight='bold'
                    css={css`
                      font-size: ${theme.fontSize(18)};
                    `}
                  >
                    {t('Previous')}
                  </Text>
                </div>
                <Text as='span'>{planDetails?.name}</Text>
                <Text as='span'>{formatCentsToCurrency(planDetails.schedule.amount)}</Text>
                <Text as='span'>
                  {termOptions.find((option) => planDetails.schedule.frequency === option.value)?.displayValue}
                </Text>
                <Text as='span'>
                  {planDetails.schedule.paymentTerm}{' '}
                  {planFrequencyLabel(planDetails.schedule.frequency, planDetails.schedule.paymentTerm)}
                </Text>
                <Text as='span'>{isoDateToDisplayLocalString(planDetails.schedule.nextPaymentAt || '')}</Text>
                <div
                  css={css`
                    display: flex;
                    flex-direction: row;
                    justify-content: start;
                    align-items: center;
                    gap: ${theme.spacing(1)};
                  `}
                >
                  <PaymentMethodName paymentMethod={planDetails.paymentMethod} showCardBrand />
                </div>
              </div>
            </div>
            <div>
              <TextareaField {...getFieldProps('memo')} label={t('Reason for changes')} />
            </div>
            <div>
              <PaymentPlanPaymentSummary monthly={getTermPayment()} date={values.paymentDate} />
            </div>
            {requireEmail && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                <Text>Send confirmation email to</Text>
                <EmailField
                  {...getFieldProps('customerEmailId')}
                  required={requireEmail}
                  placeholder={planDetails?.person?.email}
                  label={t('Email *')}
                />
              </div>
            )}
            {disableSaveBtn && (
              <Text textAlign='right' color='error' size='medium'>
                {!isComplete
                  ? t('Please enter valid details')
                  : disableSelectedACHPaymentMethod
                  ? editBillingMethodProps.achDisabledMessage
                  : noFieldsModified
                  ? t('No fields modified')
                  : t('Please enter the confirmation email')}
              </Text>
            )}
          </>
        </Modal.Body>
        <Modal.Footer
          primary={{
            onClick: getUpdateReview,
            label: isEditOperation ? t('Save') : t('Resume'),
            disabled: disableSaveBtn,
          }}
          secondary={{
            onClick: modalProps.onClose,
            label: t('Cancel'),
          }}
        ></Modal.Footer>
      </Modal>
      <Modal {...addCardModalProps} minWidth={600} maxWidth={600}>
        <EditBillingMethodModal
          handleSetBillingMethod={handleSetBillingMethod}
          onClose={addCardModalProps.onClose}
          {...editBillingMethodProps}
        />
      </Modal>
      <Modal {...confirmChangesModalProps} maxWidth={600}>
        <PlanChangesModal
          fromData={planDetails}
          toData={reviewUpdates}
          changeReason={values.memo}
          handleUpdatePaymentPlan={handleUpdatePaymentPlan}
          onClose={confirmChangesModalProps.onClose}
          viewType='confirm'
          updatedBy={`${authedUser?.firstName ?? ''} ${authedUser?.lastName ?? ''}`.trim()}
        />
      </Modal>
    </>
  );
};
